import React, { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Mail, Building2, Clock, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { LegalLayout } from "@/components/legal/LegalLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { getErrorMessage } from "@/lib/api";
import { SUPPORT_EMAIL } from "@/constants/legal";

const FADE = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.3 } };

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.subject.trim() || !form.message.trim()) {
      toast.error("Please complete all fields before sending.");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/contact", form);
      toast.success(res.data?.message || "Message sent. We'll be in touch soon.");
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not send your message. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <LegalLayout
      slug="contact"
      title="Contact us"
      subtitle="Have a question about HostGuard, your subscription, or privacy? Send us a message and our team will get back to you."
      lastUpdated={false}
    >
      <div className="grid gap-10 lg:grid-cols-[1fr_320px]">
        {/* Form */}
        <div>
          {sent ? (
            <motion.div
              {...FADE}
              className="flex flex-col items-start gap-3 rounded-xl border bg-card p-8"
              data-testid="contact-success"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[hsl(var(--risk-low))]/10">
                <CheckCircle2 className="h-6 w-6 text-[hsl(var(--risk-low))]" />
              </span>
              <h3 className="font-display text-lg font-semibold">Message received</h3>
              <p className="text-sm text-muted-foreground">
                Thanks for reaching out. We typically respond within 2 business days.
              </p>
              <Button variant="outline" size="sm" onClick={() => setSent(false)} data-testid="contact-send-another">
                Send another message
              </Button>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="space-y-4 rounded-xl border bg-card p-6 sm:p-8" data-testid="contact-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="name">Name</Label>
                  <Input id="name" value={form.name} onChange={set("name")} placeholder="Your name" required data-testid="contact-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" required data-testid="contact-email-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="subject">Subject</Label>
                <Input id="subject" value={form.subject} onChange={set("subject")} placeholder="How can we help?" required data-testid="contact-subject-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="message">Message</Label>
                <Textarea id="message" value={form.message} onChange={set("message")} placeholder="Tell us a little more..." rows={6} required data-testid="contact-message-input" />
              </div>
              <Button type="submit" className="gap-2" disabled={loading} data-testid="contact-submit-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {loading ? "Sending..." : "Send message"}
              </Button>
            </form>
          )}
        </div>

        {/* Side info */}
        <aside className="space-y-5">
          <div className="rounded-xl border bg-card p-5" data-testid="contact-support-block">
            <Mail className="h-5 w-5 text-primary" />
            <h3 className="mt-3 font-display text-base font-semibold">Support email</h3>
            <p className="mt-1 text-sm text-muted-foreground">For general questions and account help.</p>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="mt-2 inline-block text-sm font-medium text-primary hover:underline" data-testid="contact-support-email">
              {SUPPORT_EMAIL}
            </a>
          </div>

          <div className="rounded-xl border bg-card p-5">
            <Building2 className="h-5 w-5 text-primary" />
            <h3 className="mt-3 font-display text-base font-semibold">Business contact</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              HostGuard Australia<br />
              Short-stay risk assessment platform<br />
              Australia
            </p>
          </div>

          <div className="rounded-xl border bg-card p-5">
            <Clock className="h-5 w-5 text-primary" />
            <h3 className="mt-3 font-display text-base font-semibold">Response time</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              We aim to respond to all enquiries within <strong className="text-foreground">2 business days</strong>.
            </p>
          </div>
        </aside>
      </div>
    </LegalLayout>
  );
}
